import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { AllCommunityModule, ColDef, ModuleRegistry } from "ag-grid-community";
import { StatusBarModule } from "ag-grid-enterprise";
import clsx from "clsx";
import { LicenseManager } from "ag-grid-enterprise";
import { investmentMemoGridTheme } from "./agGridTheme";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { FinanceLabField } from "@/entities/FinanceLab";
import dayjs from "dayjs";
import { Metric } from "@/entities/Finance/Finance.model";
import { useAtomValue } from "jotai";
import { showDataSourcesAtom } from "../state";

LicenseManager.setLicenseKey(process.env.AG_LICENSE_KEY || "");

ModuleRegistry.registerModules([AllCommunityModule, StatusBarModule]);

interface FinanceLabMetricTableProps {
    metric: Metric | undefined;
    title: string;
}

export function FinanceLabMetricTable({
    metric,
    title,
}: FinanceLabMetricTableProps) {
    const showDataSources = useAtomValue(showDataSourcesAtom);

    const label = useMemo(() => {
        return metric?.info?.derived?.label || null;
    }, [metric?.info]);

    const isRecurringPayment = useMemo(() => {
        return label === FinanceLabField.DEAL_RECURRING_PAYMENT;
    }, [label]);
    const currentPeriodRecurringPayment = metric?.values?.find(
        (item) => item.period === dayjs(new Date()).format("YYYY-MM"),
    );
    const gridRef = useRef<AgGridReact>(null);
    const currentPeriodRecurringPaymentRef = useRef<HTMLSpanElement>(null);
    const drawerContainerRef = useRef<HTMLDivElement>(null);

    const columnDefs = useMemo<ColDef[]>(() => {
        return [
            {
                field: "period",
                resizable: false,
                flex: 0,
                wrapText: true,
                autoHeight: true,
                cellRenderer: ({ data }) => {
                    return isRecurringPayment &&
                        data?.period ===
                            currentPeriodRecurringPayment?.period ? (
                        <span
                            className="body-s font-bold"
                            ref={currentPeriodRecurringPaymentRef}
                        >
                            {data?.period || ""}
                        </span>
                    ) : (
                        <span className="body-s">{data?.period || ""}</span>
                    );
                },
            },
            {
                field: "value",
                resizable: false,
                pinned: "right",
                flex: 1,
                wrapText: true,
                autoHeight: true,
                cellRenderer: ({ data }) => {
                    const displayValue =
                        data?.formattedValue ?? data?.value ?? "";
                    return isRecurringPayment ? (
                        <span
                            className="body-s"
                            dangerouslySetInnerHTML={{
                                __html: displayValue,
                            }}
                        />
                    ) : (
                        <span className="body-s">{displayValue}</span>
                    );
                },
            },
        ];
    }, []);

    const statusBar = useMemo(() => {
        return {
            statusPanels: [
                {
                    statusPanel: "agAggregationComponent",
                    statusPanelParams: {
                        aggFuncs: ["avg", "sum"],
                    },
                    align: "right",
                },
            ],
        };
    }, []);

    const rowData = useMemo(() => {
        if (!metric?.values) return [];

        const filteredData = metric.values
            .filter((item) => item.value !== null && item.value !== undefined)
            .sort((a, b) => a.period.localeCompare(b.period))
            .reverse();

        if (isRecurringPayment) {
            return filteredData.map((item) => {
                const formattedValue = item.formattedValue || "";
                if (
                    typeof formattedValue === "string" &&
                    formattedValue.includes("(")
                ) {
                    const parts = formattedValue.split("(");
                    const mainValue = parts[0].trim();
                    const detailValue = `(${parts.slice(1).join("(")}`;
                    return {
                        ...item,
                        formattedValue: `${mainValue} 
                        <br />${detailValue}`,
                    };
                }
                return item;
            });
        }

        return filteredData;
    }, [metric, isRecurringPayment]);

    const descriptionText = useMemo(() => {
        return metric?.description?.description || null;
    }, [metric?.description]);

    const metricName = useMemo(() => {
        return metric?.description?.name || null;
    }, [metric?.description]);

    const resamplingMethod = useMemo(() => {
        return metric?.description?.resamplingMethod || null;
    }, [metric?.description]);

    const fieldType = useMemo(() => {
        return metric?.description?.fieldType || null;
    }, [metric?.description]);

    const localLabel = useMemo(() => {
        return metric?.info?.derived?.localLabel || null;
    }, [metric?.info]);

    const asOfDate = useMemo(() => {
        const dateString = metric?.info?.derived?.asOfDate;
        if (!dateString) return null;

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return null;
        }
    }, [metric?.info]);

    const hasData = rowData.length > 0;

    const scrollToCurrentPeriod = () => {
        if (isRecurringPayment && currentPeriodRecurringPayment) {
            setTimeout(() => {
                const rowIndex = rowData.findIndex(
                    (item) =>
                        item.period === currentPeriodRecurringPayment.period,
                );

                if (
                    rowIndex !== -1 &&
                    currentPeriodRecurringPaymentRef.current
                ) {
                    currentPeriodRecurringPaymentRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }, 200);
        } else {
            setTimeout(() => {
                drawerContainerRef.current?.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            }, 200);
        }
    };

    const handleFirstDataRendered = () => {
        scrollToCurrentPeriod();
    };

    const handleModelUpdated = () => {
        scrollToCurrentPeriod();
    };

    return (
        <div
            className={clsx(
                "bg-default",
                "flex size-full flex-col gap-4 overflow-auto",
            )}
            ref={drawerContainerRef}
        >
            <div className="pt-4">
                <RightSidebarButton label="Close" iconDirection="right" />
            </div>
            <div className="size-full">
                <h1 className="pageheading mb-1">{title}</h1>
                {metricName && metricName !== title && (
                    <p className="subdued body-micro mb-2">{metricName}</p>
                )}
                {asOfDate && (
                    <p className="subdued body-micro mb-1">As of {asOfDate}</p>
                )}
                {showDataSources && (
                    <>
                        {resamplingMethod && (
                            <p className="subdued body-micro mb-1">
                                Resampling: {resamplingMethod}
                            </p>
                        )}
                        {fieldType && (
                            <p className="subdued body-micro mb-1">
                                Field type: {fieldType}
                            </p>
                        )}
                        {label && (
                            <p className="subdued body-micro mb-1">
                                Label: {label}
                            </p>
                        )}
                        {localLabel && (
                            <p className="subdued body-micro mb-1">
                                Local label: {localLabel}
                            </p>
                        )}
                    </>
                )}
                {descriptionText && (
                    <p className="subdued mb-2 font-[300] italic">
                        {descriptionText}
                    </p>
                )}
                <div className="mt-6 size-full">
                    {hasData ? (
                        <AgGridReact
                            ref={gridRef}
                            theme={investmentMemoGridTheme}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            statusBar={statusBar}
                            domLayout="autoHeight"
                            onFirstDataRendered={handleFirstDataRendered}
                            onModelUpdated={handleModelUpdated}
                        />
                    ) : (
                        <div className="flex items-center justify-center py-12">
                            <p className="subdued body-s">Data not available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
